import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    model,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { PricingStore } from "../s25.event.pricing.store";
import { isDefined, isUndefined } from "../../../../util/core";
import { S25Number } from "../../../../util/core/s25.number";
import { S25Error } from "../../../../util/core/s25.error";
import { LineItem } from "../../../../pojo/Pricing";
import { S25LoadingInlineStaticComponent } from "../../../s25-loading/s25.loading.inline.static.component";
import { S25Const } from "../../../../util/s25-const";
import { S25ItemI } from "../../../../pojo/S25ItemI";

@Component({
    selector: "s25-ng-pricing-rate-group-dropdown",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25LoadingInlineStaticComponent],
    template: ` @if (canEdit()) {
            @if (!isBillingItem() || !tableLoading()) {
                <select
                    class="cn-form__control"
                    [class.error]="!!error()"
                    [attr.aria-label]="'Select Rate Group'"
                    [(ngModel)]="selectedRateGroup"
                    (change)="updateRateGroup()"
                >
                    @if (!isBillingItem()) {
                        <option [ngValue]="undefined">Choose a Rate Group</option>
                    }
                    @for (group of displayedRateGroups(); track group.itemId) {
                        <option [ngValue]="group">{{ group.itemName }}</option>
                    }
                </select>
            } @else if (tableLoading()) {
                <s25-ng-loading-inline-static />
            }

            @if (error()) {
                <p class="error-message" role="alert">{{ error() }}</p>
            }
        } @else if (!canEdit() && isBillingItem()) {
            <span class="rate-group-readonly">{{ selectedRateGroup()?.itemName || "" }}</span>
        }`,
    styles: `
        :host {
            display: block;
        }

        .error-message {
            color: var(--color--danger);
            font-size: 0.8rem;
        }

        .cn-form__control.error {
            border-bottom: 2px solid var(--color--danger) !important;
        }

        .rate-group-readonly {
            display: block;
        }

        @media (max-width: 640px) {
            .cn-form__control {
                width: 100%;
            }
        }
    `,
})
export class S25PricingRateGroupDropdown {
    readonly lineItem = input<LineItem | undefined>(undefined);
    readonly panelLoading = model(false);
    /** Set where the rate group only reports what is billed, as the invoice summary does. */
    readonly readOnly = input(false);

    readonly isBillingItem = computed(() => isDefined(this.lineItem()));

    private readonly pricingStore = inject(PricingStore);

    readonly error = signal("");
    readonly hasRatePerm = this.pricingStore.fullRatePerm;
    readonly tableLoading = signal(false);

    private readonly invoiceId = computed(() => this.lineItem()?.billId ?? this.pricingStore.evBillId());

    readonly canEdit = computed(
        () =>
            !this.readOnly() &&
            this.pricingStore.canEditPricing() &&
            !this.pricingStore.invoiceById().get(this.invoiceId()!)?.billDefn?.completed,
    );

    readonly displayedRateGroups = computed(() => {
        const lineItemRateGroupId = this.lineItem()?.rateGroupId;
        return this.pricingStore
            .allRateGroups()
            .filter((group) => this.hasRatePerm() || group.isActive || group.itemId === lineItemRateGroupId);
    });

    selectedRateGroup = linkedSignal<S25ItemI | undefined>(() => {
        const lineItem = this.lineItem(),
            displayedRateGroups = this.displayedRateGroups();
        if (!lineItem || !displayedRateGroups?.length) return undefined;

        return displayedRateGroups.find((group) => group.itemId === lineItem.rateGroupId);
    });

    async updateRateGroup() {
        const invoiceId = this.invoiceId(),
            selectedRateGroup = this.selectedRateGroup();
        const rateGroupId = S25Number.parseInt(selectedRateGroup?.itemId);
        if (isUndefined(rateGroupId) || isUndefined(invoiceId)) return;
        this.error.set("");

        if (this.isBillingItem()) {
            this.tableLoading.set(true);
            const { error } = await this.pricingStore.updateRateGroup({
                invoiceId: invoiceId!,
                rateGroupId,
                lineItem: this.lineItem(),
            });
            if (error) this.error.set(S25Error.extract(error, S25Const.defaultErrorMessage));
            this.tableLoading.set(false);
        } else {
            this.panelLoading.set(true);
            const { error } = await this.pricingStore.updateRateGroup({ invoiceId: invoiceId!, rateGroupId });
            if (error) this.error.set(S25Error.extract(error, S25Const.defaultErrorMessage));
            this.selectedRateGroup.set(undefined);
            this.panelLoading.set(false);
        }
    }
}
